import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, CheckCircle2, XCircle, ShieldCheck, Save, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/Quote";
import FileUpload from "@/components/FileUpload";
import api, { fileUrl } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const useList = (url) => { const [d, setD] = useState([]); const load = () => api.get(url).then((r) => setD(r.data)).catch(() => {}); useEffect(() => { load(); }, [url]); return [d, load]; }; // eslint-disable-line
const Card = ({ children, className = "" }) => <div className={`rounded-2xl border border-border bg-card p-4 ${className}`}>{children}</div>;

const CardHeader = ({ children }) => (
  <div className="mb-4">{children}</div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-base font-semibold">{children}</h3>
);

const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);



// ---------- Homepage Manager ----------
export function HomepageManagerTab() {
  const [sections, setSections] = useState([]);
  const [locationRequired, setLocationRequired] = useState(true);
  const [defaultRadius, setDefaultRadius] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/homepage");
      setSections(Array.isArray(r.data?.sections) ? r.data.sections : []);
      setLocationRequired(r.data?.location_permission_required !== false);
      setDefaultRadius(Number(r.data?.default_radius_km ?? 10));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load homepage configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateSection = (index, patch) => {
    setSections((prev) =>
      prev.map((section, i) =>
        i === index ? { ...section, ...patch } : section
      )
    );
  };

  const moveSection = (index, direction) => {
    setSections((prev) => {
      const next = [...prev];
      const target = index + direction;

      if (target < 0 || target >= next.length) return prev;

      [next[index], next[target]] = [next[target], next[index]];

      return next.map((section, i) => ({
        ...section,
        order: (i + 1) * 10,
      }));
    });
  };

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        section_id: `custom-section-${Date.now()}`,
        section_type: "custom",
        title: "New Homepage Section",
        description: "",
        enabled: true,
        order: (prev.length + 1) * 10,
        layout: "default",
        cta_text: "",
        cta_link: "",
        image: "",
        radius_km: null,
        location_mode: "global",
        schedule: null,
        config: {},
      },
    ]);
  };

  const removeSection = (index) => {
    setSections((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((section, i) => ({
          ...section,
          order: (i + 1) * 10,
        }))
    );
  };

  const save = async () => {
    setSaving(true);

    try {
      const normalized = sections.map((section, index) => ({
        ...section,
        section_id: String(section.section_id || `section-${index + 1}`).trim(),
        section_type: String(section.section_type || "custom").trim(),
        title: section.title || "",
        description: section.description || "",
        enabled: section.enabled !== false,
        order: Number(section.order || (index + 1) * 10),
        layout: section.layout || "default",
        cta_text: section.cta_text || "",
        cta_link: section.cta_link || "",
        image: section.image || "",
        radius_km:
          section.radius_km === "" || section.radius_km == null
            ? null
            : Number(section.radius_km),
        location_mode: section.location_mode || "global",
        schedule: section.schedule ?? null,
        config:
          section.config && typeof section.config === "object"
            ? section.config
            : {},
      }));

      await api.put("/admin/homepage", {
        sections: normalized,
        location_permission_required: locationRequired,
        default_radius_km: Number(defaultRadius) || 10,
      });

      setSections(normalized);
      toast.success("Homepage configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save homepage configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-homepage-manager">

      <Card className="overflow-hidden">
        <div className="border-b bg-gradient-to-r from-purple-50 via-white to-blue-50 px-5 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Home className="h-5 w-5 text-purple-600" />
                <h2 className="text-xl font-bold">Homepage Manager</h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Control the homepage sections, visibility, layout and location behavior.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={load}
                disabled={saving}
              >
                <Loader2 className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Reload
              </Button>

              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={addSection}
                disabled={saving}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Section
              </Button>

              <Button
                type="button"
                className="rounded-xl bg-gradient-to-r from-purple-600 to-blue-600"
                onClick={save}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 p-5 md:grid-cols-2">
          <div className="rounded-2xl border bg-card p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold">Location Permission</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Ask customers for location to show nearby vendors and content.
                </div>
              </div>

              <Switch
                checked={locationRequired}
                onCheckedChange={setLocationRequired}
              />
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-4">
            <label className="text-sm font-semibold">Default Search Radius (KM)</label>
            <Input
              type="number"
              min="1"
              max="500"
              className="mt-2 rounded-xl"
              value={defaultRadius}
              onChange={(e) => setDefaultRadius(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Used when a section does not specify its own radius.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold">Homepage Sections</h3>
          <p className="text-sm text-muted-foreground">
            {sections.length} section{sections.length === 1 ? "" : "s"} configured
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {sections.map((section, index) => (
          <Card
            key={`${section.section_id}-${index}`}
            className={`overflow-hidden transition-all ${
              section.enabled === false ? "opacity-70" : ""
            }`}
          >
            <div className="border-b bg-muted/20 px-4 py-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 text-sm font-bold text-white">
                    {index + 1}
                  </div>

                  <div>
                    <div className="font-semibold">
                      {section.title || "Untitled Section"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {section.section_type || "custom"} · ID: {section.section_id}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => moveSection(index, -1)}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => moveSection(index, 1)}
                    disabled={index === sections.length - 1}
                  >
                    ↓
                  </Button>

                  <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5">
                    <span className="text-xs font-medium">
                      {section.enabled === false ? "Disabled" : "Enabled"}
                    </span>
                    <Switch
                      checked={section.enabled !== false}
                      onCheckedChange={(checked) =>
                        updateSection(index, { enabled: checked })
                      }
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-red-600 hover:text-red-700"
                    onClick={() => removeSection(index)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid gap-4 p-5 lg:grid-cols-2">

              <div>
                <label className="text-sm font-medium">Section Title</label>
                <Input
                  className="mt-1.5 rounded-xl"
                  value={section.title || ""}
                  onChange={(e) =>
                    updateSection(index, { title: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium">Section Type</label>
                <Input
                  className="mt-1.5 rounded-xl"
                  value={section.section_type || ""}
                  onChange={(e) =>
                    updateSection(index, { section_type: e.target.value })
                  }
                />
              </div>

              <div className="lg:col-span-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  className="mt-1.5 min-h-[90px] rounded-xl"
                  value={section.description || ""}
                  onChange={(e) =>
                    updateSection(index, { description: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium">Layout</label>
                <select
                  className="mt-1.5 flex h-10 w-full rounded-xl border bg-background px-3 text-sm"
                  value={section.layout || "default"}
                  onChange={(e) =>
                    updateSection(index, { layout: e.target.value })
                  }
                >
                  <option value="default">Default</option>
                  <option value="hero">Hero</option>
                  <option value="grid">Grid</option>
                  <option value="cards">Cards</option>
                  <option value="steps">Steps</option>
                  <option value="ticker">Ticker</option>
                  <option value="carousel">Carousel</option>
                  <option value="nearby">Nearby</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Order</label>
                <Input
                  type="number"
                  className="mt-1.5 rounded-xl"
                  value={section.order ?? ""}
                  onChange={(e) =>
                    updateSection(index, { order: Number(e.target.value) })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium">CTA Text</label>
                <Input
                  className="mt-1.5 rounded-xl"
                  placeholder="Explore"
                  value={section.cta_text || ""}
                  onChange={(e) =>
                    updateSection(index, { cta_text: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium">CTA Link</label>
                <Input
                  className="mt-1.5 rounded-xl"
                  placeholder="/explore"
                  value={section.cta_link || ""}
                  onChange={(e) =>
                    updateSection(index, { cta_link: e.target.value })
                  }
                />
              </div>

              <div className="lg:col-span-2">
                <label className="text-sm font-medium">Image URL</label>
                <Input
                  className="mt-1.5 rounded-xl"
                  placeholder="https://..."
                  value={section.image || ""}
                  onChange={(e) =>
                    updateSection(index, { image: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium">Location Mode</label>
                <select
                  className="mt-1.5 flex h-10 w-full rounded-xl border bg-background px-3 text-sm"
                  value={section.location_mode || "global"}
                  onChange={(e) =>
                    updateSection(index, { location_mode: e.target.value })
                  }
                >
                  <option value="global">Global</option>
                  <option value="nearby">Nearby</option>
                  <option value="city">City</option>
                  <option value="area">Area</option>
                  <option value="pincode">Pincode</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Section Radius (KM)</label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  className="mt-1.5 rounded-xl"
                  placeholder="Default"
                  value={section.radius_km ?? ""}
                  onChange={(e) =>
                    updateSection(index, { radius_km: e.target.value })
                  }
                />
              </div>

            </div>
          </Card>
        ))}

        {sections.length === 0 && (
          <Card className="p-10 text-center">
            <Home className="mx-auto h-10 w-10 text-muted-foreground" />
            <h3 className="mt-3 font-semibold">No homepage sections</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Add a section to start building the homepage.
            </p>
          </Card>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          className="rounded-xl bg-gradient-to-r from-purple-600 to-blue-600"
          onClick={save}
          disabled={saving}
        >
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Homepage
        </Button>
      </div>
    </div>
  );
}

// ---------- Categories (CRUD + theme) ----------
export function CategoriesTab({ categories, reload }) {
  const [f, setF] = useState({ name: "", description: "", icon: "Sparkles", subcategories: "", amenities: "", accent: "#7C3AED" });
  const save = async () => {
    try { await api.post("/admin/categories", { name: f.name, description: f.description, icon: f.icon, subcategories: f.subcategories.split(",").map((s) => s.trim()).filter(Boolean), amenities: f.amenities.split(",").map((s) => s.trim()).filter(Boolean), theme: { accent: f.accent, gradient: "from-blue-600 via-purple-600 to-pink-500", bg_soft: f.accent + "14", glow: f.accent + "40" }, order: categories.length + 1 }); toast.success("Category saved"); setF({ ...f, name: "" }); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const upd = async (slug, body) => { await api.put(`/admin/categories/${slug}`, body); reload(); };
  return (
    <div className="space-y-4" data-testid="admin-categories">
      <Card className="grid sm:grid-cols-3 gap-3">
        <Input data-testid="cat-name" placeholder="Category name" className="rounded-xl" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input placeholder="Description" className="rounded-xl" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <div className="flex gap-2"><Input type="color" className="rounded-xl w-14 p-1" value={f.accent} onChange={(e) => setF({ ...f, accent: e.target.value })} /><Input placeholder="Icon (lucide name)" className="rounded-xl" value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} /></div>
        <Input placeholder="Subcategories, comma separated" className="rounded-xl" value={f.subcategories} onChange={(e) => setF({ ...f, subcategories: e.target.value })} />
        <Input placeholder="Amenities, comma separated" className="rounded-xl" value={f.amenities} onChange={(e) => setF({ ...f, amenities: e.target.value })} />
        <Button data-testid="cat-save-btn" onClick={save} disabled={!f.name} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500"><Plus className="h-4 w-4 mr-1" /> Add / update category</Button>
      </Card>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((c) => (
          <Card key={c.slug} className="p-0 overflow-hidden">
            <div className="h-3" style={{ background: `linear-gradient(to right, ${c.theme?.accent}, #EC4899)` }} />
            <div className="p-4 space-y-2">
              <div className="flex justify-between items-center"><div className="font-display font-bold">{c.name}</div><Switch checked={c.active} onCheckedChange={(v) => upd(c.slug, { active: v })} /></div>
              <div className="text-xs text-muted-foreground">{c.subcategories?.length} subcategories · {c.amenities?.length} amenities · order {c.order}</div>
              <div className="flex items-center gap-2"><input type="color" data-testid={`theme-${c.slug}`} value={c.theme?.accent || "#7C3AED"} onChange={(e) => upd(c.slug, { theme: { ...c.theme, accent: e.target.value, bg_soft: e.target.value + "14", glow: e.target.value + "40" } })} className="h-7 w-10 rounded" /><code className="text-xs">{c.theme?.accent}</code></div>
              <Textarea className="rounded-xl text-xs min-h-8" defaultValue={c.subcategories?.join(", ")} onBlur={(e) => upd(c.slug, { subcategories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Subcategories" />
              <Textarea className="rounded-xl text-xs min-h-8" defaultValue={c.amenities?.join(", ")} onBlur={(e) => upd(c.slug, { amenities: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Amenities" />
            </div>
          </Card>))}
      </div>
    </div>
  );
}

// ---------- Banners / Ads ----------
export function BannersTab({ categories }) {
  const [list, load] = useList("/admin/banners");
  const [locations, setLocations] = useState([]);
  const [f, setF] = useState({
    title: "",
    subtitle: "",
    cta: "Explore",
    image: [],
    category_slug: "",
    city: "Hyderabad",
    area: "",
    link: "",
    priority: 1,
    kind: "banner",
    starts_at: "",
    ends_at: "",
    target_type: "all",
    target_states: [],
    target_cities: [],
    target_areas: [],
  });

  useEffect(() => {
    api.get("/locations/tree")
      .then((r) => setLocations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLocations([]));
  }, []);

  // Location hierarchy: State -> City -> Area.
  // /locations/tree returns city records with state names and parent_id,
  // plus area records linked to their city.
  const cities = locations.filter(
    (x) => x && x.type === "city" && x.active !== false
  );

  const areas = locations.filter(
    (x) => x && x.type === "area" && x.active !== false
  );

  const states = Array.from(
    new Map(
      cities
        .filter((c) => c.state)
        .map((c) => [
          c.parent_id,
          {
            id: c.parent_id,
            name: c.state,
            type: "state",
            active: true,
          },
        ])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const selectedStateNames = f.target_states || [];
  const selectedCityNames = f.target_cities || [];

  const selectedStateIds = new Set(
    states
      .filter((s) => selectedStateNames.includes(s.name))
      .map((s) => s.id)
  );

  const targetCities = cities.filter(
    (c) => selectedStateIds.has(c.parent_id)
  );

  const selectedCityIds = new Set(
    targetCities
      .filter((c) => selectedCityNames.includes(c.name))
      .map((c) => c.id)
  );

  const targetAreas = areas.filter(
    (a) => selectedCityIds.has(a.parent_id)
  );

  const multipleTargetCities = cities.filter((c) =>
    selectedStateIds.has(c.parent_id)
  );

  const multipleSelectedCityIds = new Set(
    selectedCityNames
      .map((name) => cities.find((c) => c.name === name)?.id)
      .filter(Boolean)
  );

  const multipleTargetAreas = areas.filter((a) =>
    multipleSelectedCityIds.has(a.parent_id)
  );

  const save = async () => {
    if (!f.image[0]) {
      toast.error("Upload an image");
      return;
    }

    try {
      await api.post("/admin/banners", {
        ...f,
        image: `/api/files/${f.image[0]}`,
        category_slug: f.category_slug || null,
        city: null,
        area: null,
        link: f.link || "#",
        starts_at: f.starts_at || null,
        ends_at: f.ends_at || null,
        target_type: f.target_type || "all",
        target_states: f.target_states || [],
        target_cities: f.target_cities || [],
        target_areas: f.target_areas || [],
      });

      toast.success("Banner created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className="space-y-4" data-testid="admin-banners">
      <Card className="grid sm:grid-cols-3 gap-3">
        <Input data-testid="banner-title" placeholder="Title" className="rounded-xl" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <Input placeholder="Subtitle" className="rounded-xl" value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} />
        <Input placeholder="CTA text" className="rounded-xl" value={f.cta} onChange={(e) => setF({ ...f, cta: e.target.value })} />
        <select className="rounded-xl border border-border bg-background px-3 py-2 text-sm" value={f.category_slug} onChange={(e) => setF({ ...f, category_slug: e.target.value })}><option value="">All categories</option>{categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select>

        <div className="sm:col-span-3 rounded-2xl border border-border bg-muted/20 p-4 space-y-3">
          <div>
            <div className="text-sm font-semibold">Banner Targeting</div>
            <div className="text-xs text-muted-foreground">
              Choose where this individual banner should appear.
            </div>
          </div>

          <select
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
            value={f.target_type}
            onChange={(e) => setF({
              ...f,
              target_type: e.target.value,
              target_states: [],
              target_cities: [],
              target_areas: [],
            })}
          >
            <option value="all">All India</option>
            <option value="state">State</option>
            <option value="city">City</option>
            <option value="area">Area</option>
            <option value="multiple">Multiple Locations</option>
          </select>

          {f.target_type === "state" && (
            <select
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
              value={f.target_states?.[0] || ""}
              onChange={(e) => setF({
                ...f,
                target_states: e.target.value ? [e.target.value] : [],
                target_cities: [],
                target_areas: [],
              })}
            >
              <option value="">Select state</option>
              {states.map((s) => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          )}

          {(f.target_type === "city" || f.target_type === "area") && (
            <div className="grid sm:grid-cols-2 gap-3">
              <select
                className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
                value={f.target_states?.[0] || ""}
                onChange={(e) => setF({
                  ...f,
                  target_states: e.target.value ? [e.target.value] : [],
                  target_cities: [],
                  target_areas: [],
                })}
              >
                <option value="">Select state</option>
                {states.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>

              <select
                className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
                value={f.target_cities?.[0] || ""}
                onChange={(e) => setF({
                  ...f,
                  target_cities: e.target.value ? [e.target.value] : [],
                  target_areas: [],
                })}
                disabled={!f.target_states?.length}
              >
                <option value="">Select city</option>
                {targetCities.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>

              {f.target_type === "area" && (
                <select
                  className="rounded-xl border border-border bg-background px-3 py-2 text-sm sm:col-span-2"
                  value={f.target_areas?.[0] || ""}
                  onChange={(e) => setF({
                    ...f,
                    target_areas: e.target.value ? [e.target.value] : [],
                  })}
                  disabled={!f.target_cities?.length}
                >
                  <option value="">Select area</option>
                  {targetAreas.map((a) => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {f.target_type === "multiple" && (
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <div className="text-xs font-medium mb-1">States</div>
                <select
                  multiple
                  size={6}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                  value={f.target_states || []}
                  onChange={(e) => {
                    const values = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    );
                    setF({
                      ...f,
                      target_states: values,
                      target_cities: [],
                      target_areas: [],
                    });
                  }}
                >
                  {states.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-xs font-medium mb-1">Cities</div>
                <select
                  multiple
                  size={6}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                  value={f.target_cities || []}
                  onChange={(e) => {
                    const values = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    );
                    setF({
                      ...f,
                      target_cities: values,
                      target_areas: [],
                    });
                  }}
                >
                  {multipleTargetCities.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-xs font-medium mb-1">Areas</div>
                <select
                  multiple
                  size={6}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                  value={f.target_areas || []}
                  onChange={(e) => {
                    const values = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    );
                    setF({
                      ...f,
                      target_areas: values,
                    });
                  }}
                >
                  {multipleTargetAreas.map((a) => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-3 text-xs text-muted-foreground">
                Hold Ctrl/Cmd to select multiple locations. A banner matches
                when the customer's state, city, or area matches any selected
                target.
              </div>
            </div>
          )}
        </div>
        <Input placeholder="Target area (optional)" className="rounded-xl" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} />
        <Input placeholder="Link (/category/venues)" className="rounded-xl" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} />
        <select className="rounded-xl border border-border bg-background px-3 py-2 text-sm" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}><option value="banner">Banner</option><option value="ad">Advertisement</option></select>
        <div className="grid sm:grid-cols-2 gap-3 sm:col-span-2">
          <div>
            <label className="block text-sm font-medium mb-1">Starting Date</label>
            <Input
              type="date"
              className="rounded-xl"
              value={f.starts_at}
              onChange={(e) => setF({ ...f, starts_at: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Ending Date</label>
            <Input
              type="date"
              className="rounded-xl"
              value={f.ends_at}
              onChange={(e) => setF({ ...f, ends_at: e.target.value })}
            />
          </div>
        </div>
        <div className="sm:col-span-2"><FileUpload value={f.image} onChange={(v) => setF({ ...f, image: v })} max={1} purpose="banner" testId="banner-upload" /></div>
        <Button data-testid="banner-save-btn" onClick={save} disabled={!f.title} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 self-end">Create</Button>
      </Card>
      <div className="grid sm:grid-cols-2 gap-4">{list.map((b) => (
        <div key={b.id} className="rounded-2xl border border-border overflow-hidden relative h-32 group">
          <img src={fileUrl(b.image)} className="w-full h-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent p-4 flex flex-col justify-center text-white"><div className="font-display font-bold text-sm">{b.title} <span className="text-[10px] uppercase opacity-70">{b.kind}</span></div><div className="text-xs opacity-80">{b.city}{b.area ? ` · ${b.area}` : ""} · {b.category_slug || "all"} · p{b.priority} · {b.impressions || 0} views · {b.clicks || 0} clicks</div></div>
          <button onClick={async () => { await api.delete(`/admin/banners/${b.id}`); load(); }} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 text-red-500 hidden group-hover:flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>))}</div>
    </div>
  );
}

// ---------- Customers ----------
export function CustomersTab() {
  const [list, load] = useList("/admin/customers");
  const [q, setQ] = useState("");
  const act = async (u, a) => { await api.post(`/admin/customers/${u.id}/${a}`, {}); toast.success(a); load(); };
  const rows = list.filter((u) => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.phone || "").includes(q));
  return (
    <div data-testid="admin-customers">
      <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone" className="rounded-xl max-w-xs mb-4" />
      <Card className="p-0 overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Joined</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((u) => <TableRow key={u.id}><TableCell className="font-medium">{u.name}</TableCell><TableCell>{u.phone}</TableCell><TableCell>{u.email || "—"}</TableCell><TableCell className="capitalize">{u.role.replace("_", " ")}</TableCell><TableCell><StatusBadge status={u.status === "active" ? "approved" : "suspended"} /></TableCell><TableCell className="text-xs">{new Date(u.created_at).toLocaleDateString("en-IN")}</TableCell>
          <TableCell>{u.role !== "super_admin" && <div className="flex gap-1">{u.status === "active" ? <Button size="sm" variant="outline" className="h-7 text-xs rounded-lg text-red-500" onClick={() => act(u, "suspend")}>Suspend</Button> : <Button size="sm" variant="outline" className="h-7 text-xs rounded-lg" onClick={() => act(u, "activate")}>Activate</Button>}{u.role === "customer" && <Button size="sm" variant="ghost" className="h-7 text-xs rounded-lg" onClick={() => act(u, "make_admin")}>Make admin</Button>}{u.role === "admin" && <Button size="sm" variant="ghost" className="h-7 text-xs rounded-lg" onClick={() => act(u, "make_customer")}>Remove admin</Button>}</div>}</TableCell></TableRow>)}</TableBody></Table></Card>
    </div>
  );
}

// ---------- Bookings & Refunds ----------
export function BookingsTab() {
  const [list, load] = useList("/admin/bookings");
  const refund = async (b, a) => { const note = a === "reject" ? window.prompt("Reason") : null; try { await api.post(`/bookings/${b.id}/refund/${a}`, { note }); toast.success(`Refund ${a}d`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <Card className="p-0 overflow-x-auto" data-testid="admin-bookings"><Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Customer</TableHead><TableHead>Vendor</TableHead><TableHead>Event</TableHead><TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead>Refund</TableHead></TableRow></TableHeader>
      <TableBody>{list.map((b) => <TableRow key={b.id} data-testid={`admin-booking-${b.id}`}><TableCell className="font-mono text-xs">{b.code}</TableCell><TableCell>{b.customer_name}</TableCell><TableCell>{b.vendor_name}</TableCell><TableCell>{b.event_type}<div className="text-xs text-muted-foreground">{b.event_date}</div></TableCell><TableCell>{formatINR(b.total)}</TableCell><TableCell>{formatINR(b.paid_amount)}</TableCell><TableCell><StatusBadge status={b.status} /></TableCell>
        <TableCell>{["refund_requested", "disputed"].includes(b.status) && <div className="flex gap-1"><Button size="sm" className="h-7 text-xs rounded-lg bg-emerald-500" onClick={() => refund(b, "approve")}>Approve refund</Button><Button size="sm" variant="outline" className="h-7 text-xs rounded-lg" onClick={() => refund(b, "reject")}>Reject</Button></div>}</TableCell></TableRow>)}</TableBody></Table>
      {list.length === 0 && <p className="p-8 text-center text-muted-foreground">No bookings yet.</p>}</Card>
  );
}

// ---------- Payments, commissions & payouts ----------
export function BillingTab() {
  const [data, setData] = useState({
    gst_enabled: true,
    gst_rate: 18,
    cgst_rate: 9,
    sgst_rate: 9,
    igst_rate: 18,
    tax_inclusive: false,
    invoice_prefix: "INV",
    brand_name: "MakeMyEventPro",
    legal_name: "",
    gstin: "",
    registered_address: "",
    state: "Telangana",
    state_code: "36",
    billing_email: "exactmedia.in@gmail.com",
    billing_phone: "",
    website: "https://makemyeventpro.com",
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/billing-settings")
      .then((r) => setData((x) => ({ ...x, ...(r.data || {}) })))
      .catch(() => {});
  }, []);

  const set = (key, value) => {
    setSaved(false);
    setData((x) => ({ ...x, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);

    try {
      await api.put("/admin/billing-settings", {
        ...data,
        gst_rate: Number(data.gst_rate),
        cgst_rate: Number(data.cgst_rate),
        sgst_rate: Number(data.sgst_rate),
        igst_rate: Number(data.igst_rate),
      });

      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full rounded-xl border px-3 py-2 text-sm bg-background";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Billing & Invoices</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure GST, company billing details and invoice settings.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>GST Configuration</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!data.gst_enabled}
                onChange={(e) => set("gst_enabled", e.target.checked)}
              />
              Enable GST
            </label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">GST Rate (%)</label>
                <input
                  className={inputClass}
                  type="number"
                  value={data.gst_rate}
                  onChange={(e) => set("gst_rate", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium">IGST Rate (%)</label>
                <input
                  className={inputClass}
                  type="number"
                  value={data.igst_rate}
                  onChange={(e) => set("igst_rate", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium">CGST Rate (%)</label>
                <input
                  className={inputClass}
                  type="number"
                  value={data.cgst_rate}
                  onChange={(e) => set("cgst_rate", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium">SGST Rate (%)</label>
                <input
                  className={inputClass}
                  type="number"
                  value={data.sgst_rate}
                  onChange={(e) => set("sgst_rate", e.target.value)}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!data.tax_inclusive}
                onChange={(e) => set("tax_inclusive", e.target.checked)}
              />
              Prices are tax-inclusive
            </label>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Invoice Configuration</CardTitle>
          </CardHeader>

          <CardContent>
            <label className="text-sm font-medium">Invoice Prefix</label>
            <input
              className={inputClass}
              value={data.invoice_prefix || ""}
              onChange={(e) => set("invoice_prefix", e.target.value)}
              placeholder="INV"
            />

            <p className="text-xs text-muted-foreground mt-2">
              Example: INV-000001
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl lg:col-span-2">
          <CardHeader>
            <CardTitle>Company Billing Details</CardTitle>
          </CardHeader>

          <CardContent className="grid gap-4 md:grid-cols-2">
            {[
              ["brand_name", "Brand Name"],
              ["legal_name", "Legal Name"],
              ["gstin", "GSTIN"],
              ["registered_address", "Registered Address"],
              ["state", "State"],
              ["state_code", "State Code"],
              ["billing_email", "Billing Email"],
              ["billing_phone", "Billing Phone"],
              ["website", "Website"],
            ].map(([key, label]) => (
              <div
                key={key}
                className={key === "registered_address" ? "md:col-span-2" : ""}
              >
                <label className="text-sm font-medium">{label}</label>

                <input
                  className={inputClass}
                  value={data[key] || ""}
                  onChange={(e) => set(key, e.target.value)}
                />
              </div>
            ))}
          </CardContent>
        </Card>

      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save Billing Settings"}
        </Button>

        {saved && (
          <span className="text-sm text-green-600">
            Billing settings saved successfully.
          </span>
        )}
      </div>
    </div>
  );
}

export function PaymentsTab() {
  const [d, setD] = useState({ entries: [], vendors: [] });
  const load = () => api.get("/admin/ledger").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const payout = async (v) => { await api.post(`/admin/ledger/payout/${v.vendor_id}`, {}); toast.success("Payout marked as settled"); load(); };
  return (
    <div className="space-y-4" data-testid="admin-payments">
      <h3 className="font-display font-bold">Vendor settlements</h3>
      <Card className="p-0 overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead>Gross collected</TableHead><TableHead>Commission</TableHead><TableHead>Payable</TableHead><TableHead>Pending payout</TableHead><TableHead>Bank</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>{d.vendors.map((v) => <TableRow key={v.vendor_id}><TableCell className="font-medium">{v.vendor_name}</TableCell><TableCell>{formatINR(v.gross)}</TableCell><TableCell className="text-purple-600">{formatINR(v.commission)}</TableCell><TableCell>{formatINR(v.payable)}</TableCell><TableCell className="font-bold text-amber-600">{formatINR(v.pending_payout)}</TableCell><TableCell>{v.has_bank ? "✓" : "missing"}</TableCell><TableCell>{v.pending_payout > 0 && <Button size="sm" className="h-7 text-xs rounded-lg" onClick={() => payout(v)}>Mark paid out</Button>}</TableCell></TableRow>)}</TableBody></Table>
        {d.vendors.length === 0 && <p className="p-8 text-center text-muted-foreground">No settled payments yet.</p>}</Card>
      <h3 className="font-display font-bold">Ledger</h3>
      <Card className="p-0 overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Booking</TableHead><TableHead>Amount</TableHead><TableHead>Commission</TableHead><TableHead>Vendor amt</TableHead><TableHead>Payout</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
        <TableBody>{d.entries.map((e) => <TableRow key={e.id}><TableCell className="capitalize">{e.type}</TableCell><TableCell className="font-mono text-xs">{e.booking_id.slice(0, 8)}</TableCell><TableCell>{formatINR(e.amount)}</TableCell><TableCell>{e.commission != null ? formatINR(e.commission) : "—"}</TableCell><TableCell>{e.vendor_amount != null ? formatINR(e.vendor_amount) : "—"}</TableCell><TableCell>{e.payout_status || "—"}</TableCell><TableCell className="text-xs">{new Date(e.created_at).toLocaleString("en-IN")}</TableCell></TableRow>)}</TableBody></Table></Card>
    </div>
  );
}

// ---------- Coupons & offers ----------
export function CouponsTab({ categories }) {
  const [list, load] = useList("/admin/coupons");
  const [plans] = useList("/admin/plans");

  const emptyForm = {
    code: "",
    title: "",
    type: "percent",
    value: 10,
    max_discount: "",
    min_amount: "",
    category_slug: "",
    plan_slug: "",
    max_uses: "",
    expires_at: "",
  };

  const [f, setF] = useState(emptyForm);
  const [selectedCode, setSelectedCode] = useState("");

  const loadCoupon = (code) => {
    setSelectedCode(code);

    if (!code) {
      setF(emptyForm);
      return;
    }

    const coupon = list.find((c) => c.code === code);

    if (!coupon) {
      setF(emptyForm);
      return;
    }

    setF({
      code: coupon.code || "",
      title: coupon.title || "",
      type: coupon.type || "percent",
      value: coupon.value ?? 0,
      max_discount: coupon.max_discount ?? "",
      min_amount: coupon.min_amount ?? "",
      category_slug: coupon.category_slug || "",
      plan_slug: coupon.plan_slug || "",
      max_uses: coupon.max_uses ?? "",
      expires_at: coupon.expires_at
        ? new Date(coupon.expires_at).toISOString().slice(0, 10)
        : "",
    });
  };

  const save = async () => {
    try {
      await api.post("/admin/coupons", {
        ...f,
        value: Number(f.value),
        max_discount: f.max_discount ? Number(f.max_discount) : null,
        min_amount: f.min_amount ? Number(f.min_amount) : null,
        max_uses: f.max_uses ? Number(f.max_uses) : null,
        category_slug: f.category_slug || null,
        plan_slug: f.plan_slug || null,
        expires_at: f.expires_at
          ? new Date(f.expires_at).toISOString()
          : null,
      });

      toast.success(selectedCode ? "Coupon updated" : "Coupon saved");
      setSelectedCode(f.code);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const editCoupon = (coupon) => {
    loadCoupon(coupon.code);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-4" data-testid="admin-coupons">
      <Card className="grid sm:grid-cols-4 gap-3">
        <Input
          data-testid="coupon-code"
          placeholder="CODE"
          className="rounded-xl uppercase"
          value={f.code}
          onChange={(e) =>
            setF({ ...f, code: e.target.value.toUpperCase() })
          }
        />

        <Input
          placeholder="Title / offer text"
          className="rounded-xl"
          value={f.title}
          onChange={(e) => setF({ ...f, title: e.target.value })}
        />

        <select
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
          value={f.type}
          onChange={(e) => setF({ ...f, type: e.target.value })}
        >
          <option value="percent">Percent %</option>
          <option value="flat">Flat ₹</option>
        </select>

        <Input
          type="number"
          placeholder="Value"
          className="rounded-xl"
          value={f.value}
          onChange={(e) => setF({ ...f, value: e.target.value })}
        />

        <Input
          type="number"
          placeholder="Max discount ₹"
          className="rounded-xl"
          value={f.max_discount}
          onChange={(e) =>
            setF({ ...f, max_discount: e.target.value })
          }
        />

        <Input
          type="number"
          placeholder="Min amount ₹"
          className="rounded-xl"
          value={f.min_amount}
          onChange={(e) =>
            setF({ ...f, min_amount: e.target.value })
          }
        />

        <select
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
          value={f.category_slug}
          onChange={(e) =>
            setF({ ...f, category_slug: e.target.value })
          }
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
          value={selectedCode}
          onChange={(e) => loadCoupon(e.target.value)}
        >
          <option value="">All Coupons</option>
          {list.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} — {c.title || "Coupon"}
            </option>
          ))}
        </select>

        <select
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
          value={f.plan_slug}
          onChange={(e) =>
            setF({ ...f, plan_slug: e.target.value })
          }
        >
          <option value="">All plans</option>
          {plans.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.name}
            </option>
          ))}
        </select>

        <Input
          type="number"
          placeholder="Max uses"
          className="rounded-xl"
          value={f.max_uses}
          onChange={(e) =>
            setF({ ...f, max_uses: e.target.value })
          }
        />

        <Input
          type="date"
          className="rounded-xl"
          value={f.expires_at}
          onChange={(e) =>
            setF({ ...f, expires_at: e.target.value })
          }
        />

        <Button
          data-testid="coupon-save-btn"
          onClick={save}
          disabled={!f.code}
          className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500"
        >
          {selectedCode ? "Update coupon" : "Save coupon"}
        </Button>

        {selectedCode && (
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={() => {
              setSelectedCode("");
              setF(emptyForm);
            }}
          >
            New coupon
          </Button>
        )}
      </Card>

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Offer</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Uses</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {list.map((c) => (
              <TableRow key={c.code}>
                <TableCell className="font-mono font-bold">
                  {c.code}
                </TableCell>

                <TableCell>
                  {c.title || "—"}
                </TableCell>

                <TableCell>
                  {c.type === "percent"
                    ? `${c.value}%`
                    : formatINR(c.value)}
                  {c.max_discount
                    ? ` (max ${formatINR(c.max_discount)})`
                    : ""}
                </TableCell>

                <TableCell>
                  {c.uses || 0}/{c.max_uses || "∞"}
                </TableCell>

                <TableCell>
                  {c.plan_slug
                    ? `Plan: ${c.plan_slug}`
                    : c.category_slug
                    ? c.category_slug
                    : "all"}
                </TableCell>

                <TableCell className="text-xs">
                  {c.expires_at
                    ? new Date(c.expires_at).toLocaleDateString("en-IN")
                    : "—"}
                </TableCell>

                <TableCell>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-lg"
                    onClick={() => editCoupon(c)}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ---------- Reviews moderation ----------
export function ReviewsTab() {
  const [list, load] = useList("/admin/reviews");
  const act = async (r, a) => { await api.post(`/admin/reviews/${r.id}/${a}`); toast.success(`Review ${a}d`); load(); };
  return (
    <div className="space-y-3" data-testid="admin-reviews">
      {list.length === 0 && <p className="text-center text-muted-foreground py-8">No reviews.</p>}
      {list.map((r) => <Card key={r.id} className="flex flex-wrap items-center gap-3"><div className="flex-1 min-w-48"><div className="font-semibold">{r.name} · {r.rating}★ {r.verified_booking && <span className="text-[10px] text-emerald-600 font-bold">VERIFIED BOOKING</span>}</div><p className="text-sm text-muted-foreground">{r.text}</p></div><StatusBadge status={r.status} />
        <div className="flex gap-1">{r.status !== "approved" && <Button data-testid={`review-approve-${r.id}`} size="sm" className="h-8 rounded-lg bg-emerald-500" onClick={() => act(r, "approve")}>Approve</Button>}{r.status !== "rejected" && <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => act(r, "reject")}>Reject</Button>}<Button size="sm" variant="ghost" className="h-8 rounded-lg text-red-500" onClick={() => act(r, "delete")}>Delete</Button></div></Card>)}
    </div>
  );
}

// ---------- Plans ----------
export function PlansTab() {
  const emptyPlan = {
    id: null,
    name: "",
    price_monthly: 0,
    validity_days: 30,
    features: "",
    lead_limit: "",
    featured_slots: 0,
    commission_percent: "",
    active: true,
    order: 10,
  };

  const [list, load] = useList("/admin/plans");
  const [form, setForm] = useState(emptyPlan);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setForm({ ...emptyPlan });
    setEditing(false);
  };

  const editPlan = (plan) => {
    setForm({
      id: plan.id || null,
      name: plan.name || "",
      price_monthly: plan.price_monthly ?? 0,
      validity_days: plan.validity_days ?? 30,
      features: Array.isArray(plan.features) ? plan.features.join(", ") : (plan.features || ""),
      lead_limit: plan.lead_limit ?? "",
      featured_slots: plan.featured_slots ?? 0,
      commission_percent: plan.commission_percent ?? "",
      active: plan.active !== false,
      order: plan.order ?? 10,
    });
    setEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Plan name is required");
      return;
    }

    setSaving(true);

    try {
      await api.post("/admin/plans", {
        ...(form.id ? { id: form.id } : {}),
        name: form.name.trim(),
        price_monthly: Number(form.price_monthly) || 0,
        validity_days: Number(form.validity_days) || 30,
        features: form.features
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        lead_limit:
          form.lead_limit === "" || form.lead_limit === null
            ? null
            : Number(form.lead_limit),
        featured_slots: Number(form.featured_slots) || 0,
        commission_percent:
          form.commission_percent === "" || form.commission_percent === null
            ? null
            : Number(form.commission_percent),
        active: !!form.active,
        order: Number(form.order) || 10,
      });

      toast.success(editing ? "Plan updated" : "Plan created");
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save plan");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"?`)) return;

    try {
      await api.delete(`/admin/plans/${plan.slug}`);
      toast.success("Plan deleted");
      if (form.id === plan.id) resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete plan");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-plans">
      <Card className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-xl">
              {editing ? "Edit Vendor Plan" : "Create Vendor Plan"}
            </h3>
            <p className="text-sm text-muted-foreground">
              Manage pricing, validity, features and availability from Super Admin.
            </p>
          </div>

          {editing && (
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-medium">Plan Name</label>
            <Input
              placeholder="e.g. Starter"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Price (₹)</label>
            <Input
              type="number"
              min="0"
              placeholder="4500"
              value={form.price_monthly}
              onChange={(e) =>
                setForm({ ...form, price_monthly: e.target.value })
              }
            />
          </div>

          <div>
            <label className="text-sm font-medium">Validity (Days)</label>
            <Input
              type="number"
              min="1"
              placeholder="90"
              value={form.validity_days}
              onChange={(e) =>
                setForm({ ...form, validity_days: e.target.value })
              }
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm font-medium">
              Plan Features
            </label>
            <div className="mt-2 rounded-xl border p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  "Full CRM access",
                  "Leads",
                  "Customers",
                  "Quotations",
                  "Events",
                  "Calendar",
                  "Tasks",
                  "Expenses",
                  "Team",
                  "Payments",
                  "Reviews",
                  "Analytics",
                  "Notifications",
                  "Account",
                  "Offers & Discounts",
                  "Featured Visibility",
                  "Verified Badge",
                ].map((feature) => {
                  const selected = (form.features || "")
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                    .includes(feature);

                  return (
                    <div
                      key={feature}
                      className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                    >
                      <span className="text-sm font-medium">{feature}</span>
                      <Switch
                        checked={selected}
                        onCheckedChange={(checked) => {
                          const current = (form.features || "")
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean);

                          const next = checked
                            ? [...new Set([...current, feature])]
                            : current.filter((item) => item !== feature);

                          setForm({
                            ...form,
                            features: next.join(", "),
                          });
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <p className="mt-3 text-xs text-muted-foreground">
                Enable or disable features available to vendors on this plan.
                Existing custom features are preserved.
              </p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">
              Lead Limit <span className="text-muted-foreground">(blank = unlimited)</span>
            </label>
            <Input
              type="number"
              min="0"
              placeholder="Unlimited"
              value={form.lead_limit}
              onChange={(e) =>
                setForm({ ...form, lead_limit: e.target.value })
              }
            />
          </div>

          <div>
            <label className="text-sm font-medium">Featured Slots</label>
            <Input
              type="number"
              min="0"
              value={form.featured_slots}
              onChange={(e) =>
                setForm({ ...form, featured_slots: e.target.value })
              }
            />
          </div>

          <div>
            <label className="text-sm font-medium">Commission % Override</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Optional"
              value={form.commission_percent}
              onChange={(e) =>
                setForm({ ...form, commission_percent: e.target.value })
              }
            />
          </div>

          <div>
            <label className="text-sm font-medium">Display Order</label>
            <Input
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: e.target.value })
              }
            />
          </div>

          <div className="flex items-center gap-3 pt-7">
            <Switch
              checked={!!form.active}
              onCheckedChange={(checked) =>
                setForm({ ...form, active: checked })
              }
            />
            <div>
              <div className="font-medium">Active Plan</div>
              <div className="text-xs text-muted-foreground">
                Active plans can be offered to vendors.
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={save}
            disabled={saving || !form.name.trim()}
            className="bg-gradient-to-r from-purple-600 to-pink-500"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : editing ? "Update Plan" : "Create Plan"}
          </Button>

          {editing && (
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          )}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((p) => (
          <Card key={p.slug} className="relative">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-bold text-lg">{p.name}</h3>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      p.active
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="text-2xl font-extrabold text-purple-600 mt-2">
                  {formatINR(p.price_monthly)}
                </div>

                <div className="text-sm text-muted-foreground mt-1">
                  Valid for {p.validity_days ?? 30} days
                </div>
              </div>

              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => editPlan(p)}
                >
                  Edit
                </Button>

                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => deletePlan(p)}
                  className="text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <ul className="text-sm space-y-1 mt-4">
              {(p.features || []).map((x) => (
                <li key={x}>• {x}</li>
              ))}
            </ul>

            <div className="text-xs text-muted-foreground mt-4">
              Leads: {p.lead_limit ?? "Unlimited"} · Featured slots:{" "}
              {p.featured_slots ?? 0}
              {p.commission_percent != null
                ? ` · Commission ${p.commission_percent}%`
                : ""}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------- CMS pages ----------
export function PagesTab() {
  const [list, load] = useList("/admin/pages");
  const [f, setF] = useState({ slug: "", title: "", content: "", published: true });
  const save = async () => { await api.post("/admin/pages", f); toast.success("Page saved"); load(); };
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-4" data-testid="admin-pages">
      <Card className="space-y-3"><div className="grid sm:grid-cols-2 gap-3"><Input placeholder="slug (about, terms…)" className="rounded-xl" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /><Input placeholder="Title" className="rounded-xl" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div><Textarea placeholder="Content (Markdown / HTML)" className="rounded-xl min-h-48" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} /><div className="flex items-center justify-between"><label className="flex items-center gap-2 text-sm"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} /> Published</label><Button onClick={save} disabled={!f.slug || !f.title} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500">Save page</Button></div></Card>
      <div className="space-y-2">{list.map((p) => <button key={p.slug} onClick={() => setF(p)} className="w-full text-left rounded-xl border border-border bg-card px-4 py-3 text-sm hover:border-purple-300"><b>{p.title}</b> <span className="text-muted-foreground">/{p.slug}</span>{!p.published && <span className="text-[10px] text-red-400 ml-2">draft</span>}</button>)}</div>
    </div>
  );
}

// ---------- Settings (logo, splash, commission, policies) ----------
export function SettingsTab() {
  const [s, setS] = useState({}); const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(Object.fromEntries(r.data.map((x) => [x.key, x.value])))); }, []);
  const put = async (key, value) => { setBusy(true); try { await api.put("/admin/settings", { key, value }); setS({ ...s, [key]: value }); toast.success(`${key} saved`); } finally { setBusy(false); } };
  const Row = ({ k, label, type = "text", hint }) => <div className="space-y-1"><label className="text-sm font-medium">{label}</label><div className="flex gap-2"><Input data-testid={`setting-${k}`} type={type} className="rounded-xl" defaultValue={s[k] ?? ""} onBlur={(e) => e.target.value !== String(s[k] ?? "") && put(k, type === "number" ? Number(e.target.value) : e.target.value)} /></div>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-settings">
      <Card className="space-y-4"><h3 className="font-display font-bold">Brand</h3><Row k="brand_name" label="Brand name" /><Row k="support_phone" label="Support phone" /><Row k="support_email" label="Support email" />
        <div className="grid grid-cols-2 gap-4"><div><div className="text-sm font-medium mb-1">Logo</div><FileUpload value={s.logo_url ? [s.logo_url] : []} onChange={(v) => put("logo_url", v[0] || null)} max={1} purpose="brand" testId="setting-logo-upload" /></div><div><div className="text-sm font-medium mb-1">Launch / splash image</div><FileUpload value={s.splash_url ? [s.splash_url] : []} onChange={(v) => put("splash_url", v[0] || null)} max={1} purpose="brand" testId="setting-splash-upload" /></div></div></Card>
      <Card className="space-y-4"><h3 className="font-display font-bold">Commerce</h3><Row k="commission_percent" label="Platform commission %" type="number" hint="Applied to new bookings. Plans may override." />
        <div className="space-y-1"><label className="text-sm font-medium">Default cancellation policy</label><Textarea className="rounded-xl" defaultValue={s.default_cancellation_policy || ""} onBlur={(e) => put("default_cancellation_policy", e.target.value)} /></div>
        <div className="space-y-1"><label className="text-sm font-medium">Vendor terms & declaration</label><Textarea className="rounded-xl" defaultValue={s.vendor_terms || ""} onBlur={(e) => put("vendor_terms", e.target.value)} /></div>{busy && <Loader2 className="h-4 w-4 animate-spin" />}</Card>
    </div>
  );
}

// ---------- Payment Settings (mode selection; secrets stay server-side) ----------
const MODE_LABELS = { demo: "Demo Payment", razorpay_test: "Razorpay Test", razorpay_live: "Razorpay Live", none: "None" };
const Chip = ({ ok, okText = "Configured", noText = "Not Configured", testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{ok ? okText : noText}
  </span>
);

export function PaymentSettingsTab() {
  const [st, setSt] = useState(null);
  const [mode, setMode] = useState("demo");
  const [demoOn, setDemoOn] = useState(true);
  const [rzpOn, setRzpOn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState("");
  const testConn = async (m) => {
    setTesting(m);
    try { const { data } = await api.post("/admin/payment-settings/test-connection", { mode: m }); data.ok ? toast.success(data.message) : toast.error(data.message); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(""); }
  };
  const load = () => api.get("/admin/payment-settings").then((r) => {
    setSt(r.data); setMode(r.data.payment_mode === "none" ? "demo" : r.data.payment_mode);
    setDemoOn(r.data.demo_payment_enabled); setRzpOn(r.data.razorpay_enabled);
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/payment-settings", { payment_mode: mode, demo_payment_enabled: demoOn, razorpay_enabled: rzpOn });
      setSt(data); toast.success(`Payment settings saved — customers now use ${MODE_LABELS[data.payment_mode]}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save settings"); }
    finally { setBusy(false); }
  };
  if (!st) return <div className="flex justify-center py-10" data-testid="admin-payment-settings-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const testCfg = st.razorpay_test, liveCfg = st.razorpay_live;
  const modes = [
    { k: "demo", label: "Demo Payment", desc: "Simulated gateway — no real charge. For testing the checkout flow." },
    { k: "razorpay_test", label: "Razorpay Test Mode", desc: "Real Razorpay checkout UI using TEST keys. No real money moves.", ok: testCfg.configured, warn: "Razorpay Test Mode is not configured" },
    { k: "razorpay_live", label: "Razorpay Live Mode", desc: "Live payments using Razorpay LIVE keys. Real money is charged.", ok: liveCfg.configured, warn: "Razorpay Live Mode is not configured" },
    { k: "phonepe_sandbox", label: "PhonePe Sandbox", desc: "Test PhonePe checkout using Sandbox credentials. No real money is charged.", ok: true, warn: "PhonePe Sandbox is not configured" },
    { k: "phonepe_live", label: "PhonePe Live Mode", desc: "Live payments using PhonePe Live credentials. Real money is charged.", ok: true, warn: "PhonePe Live Mode is not configured" },
  ];
  const blocked = (mode === "razorpay_test" && !testCfg.configured) || (mode === "razorpay_live" && !liveCfg.configured);
  return (
    <div className="max-w-3xl space-y-4" data-testid="admin-payment-settings">
      <Card className="space-y-3">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-purple-600" /><h3 className="font-display font-bold">Payment Settings</h3></div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">Current mode:</span>
          <span data-testid="current-payment-mode" className="font-semibold">{MODE_LABELS[st.payment_mode] || "None"}</span>
          <span data-testid="checkout-availability" className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.checkout_available ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{st.checkout_available ? "Active" : "Online payment unavailable"}</span>
        </div>
        <p className="text-xs text-muted-foreground">Choose which payment method customers see at checkout. Changes take effect immediately and are recorded in the audit log.</p>
      </Card>

      <Card className="space-y-3">
        <h4 className="font-semibold text-sm">Select payment mode</h4>
        {modes.map((m) => (
          <label key={m.k} data-testid={`payment-mode-${m.k}`} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${mode === m.k ? "border-purple-500 bg-purple-50/50" : "border-border hover:border-purple-200"}`}>
            <input type="radio" name="paymode" className="mt-1 accent-purple-600" checked={mode === m.k} onChange={() => setMode(m.k)} data-testid={`payment-mode-radio-${m.k}`} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-sm">{m.label}</span>{m.k !== "demo" && <Chip ok={m.ok} testId={`payment-mode-status-${m.k}`} />}</div>
              <p className="text-xs text-muted-foreground mt-0.5">{m.desc}</p>
              {m.k !== "demo" && !m.ok && <p className="text-xs text-rose-600 mt-1">{m.warn}</p>}
            </div>
          </label>
        ))}
      </Card>

      <Card className="space-y-4">
        <div className="flex items-center justify-between gap-4"><div><div className="font-medium text-sm">Demo Payment enabled</div><p className="text-xs text-muted-foreground">Allow the simulated demo gateway at checkout.</p></div><Switch data-testid="toggle-demo-enabled" checked={demoOn} onCheckedChange={setDemoOn} /></div>
        <div className="flex items-center justify-between gap-4"><div><div className="font-medium text-sm">Razorpay enabled</div><p className="text-xs text-muted-foreground">Master switch for Razorpay checkout (test &amp; live).</p></div><Switch data-testid="toggle-razorpay-enabled" checked={rzpOn} onCheckedChange={setRzpOn} /></div>
      </Card>

      <Card className="space-y-3">
        <h4 className="font-semibold text-sm">Razorpay configuration <span className="text-xs font-normal text-muted-foreground">· secrets never leave the server</span></h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-border p-3 space-y-2" data-testid="razorpay-test-config"><div className="font-medium text-sm flex items-center justify-between">Test keys <Chip ok={testCfg.configured} /></div>
            <div className="text-xs flex justify-between items-center"><span className="text-muted-foreground">Key ID</span><Chip ok={testCfg.key_id_configured} /></div>
            <div className="text-xs flex justify-between items-center"><span className="text-muted-foreground">Key Secret</span><Chip ok={testCfg.key_secret_configured} /></div>
            <Button data-testid="test-razorpay-test" size="sm" variant="outline" className="rounded-xl w-full mt-1" disabled={testing === "razorpay_test"} onClick={() => testConn("razorpay_test")}>{testing === "razorpay_test" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test Connection"}</Button>
          </div>
          <div className="rounded-xl border border-border p-3 space-y-2" data-testid="razorpay-live-config"><div className="font-medium text-sm flex items-center justify-between">Live keys <Chip ok={liveCfg.configured} /></div>
            <div className="text-xs flex justify-between items-center"><span className="text-muted-foreground">Key ID</span><Chip ok={liveCfg.key_id_configured} /></div>
            <div className="text-xs flex justify-between items-center"><span className="text-muted-foreground">Key Secret</span><Chip ok={liveCfg.key_secret_configured} /></div>
            <Button data-testid="test-razorpay-live" size="sm" variant="outline" className="rounded-xl w-full mt-1" disabled={testing === "razorpay_live"} onClick={() => testConn("razorpay_live")}>{testing === "razorpay_live" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test Connection"}</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Razorpay Key Secrets are configured in the server environment and are never sent to the browser.</p>
      </Card>

      {blocked && <p className="text-xs text-rose-600" data-testid="payment-save-blocked">Configure the selected Razorpay key pair on the server before activating this mode.</p>}
      <Button data-testid="save-payment-settings" onClick={save} disabled={busy || blocked} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save Settings</>}
      </Button>
    </div>
  );
}


// ---------- Notifications broadcast ----------
export function IntegrationsTab() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(null);
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const integrationList = [
    {
      id: "firebase",
      name: "Firebase Authentication",
      description: "OTP authentication and Firebase server integration",
      fields: [
        {
          key: "service_account_json",
          label: "Firebase Service Account JSON",
          type: "textarea",
          secret: true,
          placeholder: "Paste Firebase service account JSON here",
        },
      ],
    },
    {
      id: "fcm",
      name: "Firebase Cloud Messaging",
      description: "Push notifications for customers, vendors and administrators",
      fields: [
        {
          key: "service_account_json",
          label: "FCM Service Account JSON",
          type: "textarea",
          secret: true,
          placeholder: "Paste Firebase service account JSON here",
        },
      ],
    },
    {
      id: "razorpay_test",
      name: "Razorpay Test",
      description: "Test payments and checkout",
      fields: [
        {
          key: "key_id",
          label: "Razorpay Test Key ID",
          placeholder: "rzp_test_XXXXXXXXXXXX",
        },
        {
          key: "key_secret",
          label: "Razorpay Test Key Secret",
          type: "password",
          secret: true,
          placeholder: "Enter Razorpay Test Key Secret",
        },
      ],
    },
    {
      id: "razorpay_live",
      name: "Razorpay Live",
      description: "Live payments and checkout",
      fields: [
        {
          key: "key_id",
          label: "Razorpay Live Key ID",
          placeholder: "rzp_live_XXXXXXXXXXXX",
        },
        {
          key: "key_secret",
          label: "Razorpay Live Key Secret",
          type: "password",
          secret: true,
          placeholder: "Enter Razorpay Live Key Secret",
        },
      ],
    },
    {
    id: "phonepe_sandbox",
    name: "PhonePe Sandbox",
    description: "Test payments and checkout using PhonePe Sandbox",
    fields: [
      {
        key: "client_id",
        label: "PhonePe Sandbox Client ID",
        placeholder: "Enter PhonePe Sandbox Client ID",
      },
      {
        key: "client_version",
        label: "PhonePe Sandbox Client Version",
        placeholder: "Enter Client Version",
      },
      {
        key: "client_secret",
        label: "PhonePe Sandbox Client Secret",
        type: "password",
        secret: true,
        placeholder: "Enter PhonePe Sandbox Client Secret",
      },
    ],
  },
  {
    id: "phonepe_live",
    name: "PhonePe Live",
    description: "Live payments and checkout using PhonePe",
    fields: [
      {
        key: "client_id",
        label: "PhonePe Live Client ID",
        placeholder: "Enter PhonePe Live Client ID",
      },
      {
        key: "client_version",
        label: "PhonePe Live Client Version",
        placeholder: "Enter Client Version",
      },
      {
        key: "client_secret",
        label: "PhonePe Live Client Secret",
        type: "password",
        secret: true,
        placeholder: "Enter PhonePe Live Client Secret",
      },
    ],
  },
  {
      id: "google_maps",
      name: "Google Maps",
      description: "Maps, pins, autocomplete and distance calculation",
      fields: [
        {
          key: "api_key",
          label: "Google Maps API Key",
          type: "password",
          secret: true,
          placeholder: "Enter Google Maps API Key",
        },
      ],
    },
    {
      id: "whatsapp",
      name: "WhatsApp",
      description: "WhatsApp API notifications and messaging",
      fields: [
        {
          key: "provider",
          label: "Provider",
          placeholder: "Meta WhatsApp Cloud API",
        },
        {
          key: "api_token",
          label: "API Access Token",
          type: "password",
          secret: true,
          placeholder: "Enter WhatsApp API token",
        },
        {
          key: "phone_number_id",
          label: "Phone Number ID",
          placeholder: "Enter WhatsApp Phone Number ID",
        },
        {
          key: "business_account_id",
          label: "Business Account ID",
          placeholder: "Enter WhatsApp Business Account ID",
        },
      ],
    },
    {
      id: "email",
      name: "Email",
      description: "Transactional emails and system notifications",
      fields: [
        {
          key: "provider",
          label: "Provider",
          placeholder: "Resend / SendGrid / SMTP",
        },
        {
          key: "api_key",
          label: "API Key",
          type: "password",
          secret: true,
          placeholder: "Enter email provider API key",
        },
        {
          key: "smtp_host",
          label: "SMTP Host",
          placeholder: "smtp.example.com",
        },
        {
          key: "smtp_port",
          label: "SMTP Port",
          placeholder: "587",
        },
        {
          key: "smtp_user",
          label: "SMTP Username",
          placeholder: "SMTP username",
        },
        {
          key: "smtp_password",
          label: "SMTP Password",
          type: "password",
          secret: true,
          placeholder: "SMTP password",
        },
        {
          key: "from_email",
          label: "From Email",
          placeholder: "noreply@makemyeventpro.com",
        },
      ],
    },
    {
      id: "sms",
      name: "SMS",
      description: "OTP and SMS notifications",
      fields: [
        {
          key: "provider",
          label: "Provider",
          placeholder: "Twilio / MSG91 / Other",
        },
        {
          key: "auth_token",
          label: "Auth Token",
          type: "password",
          secret: true,
          placeholder: "Enter SMS provider auth token",
        },
        {
          key: "api_key",
          label: "API Key",
          type: "password",
          secret: true,
          placeholder: "Enter SMS provider API key",
        },
        {
          key: "sender_id",
          label: "Sender ID",
          placeholder: "MMEP",
        },
      ],
    },
  ];

  const loadStatus = async () => {
    try {
      const r = await api.get("/admin/integrations-status");
      setStatus(r.data || {});
    } catch (e) {
      console.error("Integration status error:", e);
    }
  };

  const loadConfig = async (integration) => {
    if (configs[integration]) {
      setOpen(integration);
      return;
    }

    try {
      const r = await api.get(`/admin/integration-config/${integration}`);

      setConfigs((prev) => ({
        ...prev,
        [integration]: r.data?.config || {},
      }));

      setOpen(integration);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Unable to load integration configuration"
      );
    }
  };

  const updateField = (integration, key, value) => {
    setConfigs((prev) => ({
      ...prev,
      [integration]: {
        ...(prev[integration] || {}),
        [key]: value,
      },
    }));
  };

  const saveConfig = async (integration) => {
    setSaving(integration);

    try {
      await api.put("/admin/integration-config", {
        integration,
        config: configs[integration] || {},
      });

      await loadStatus();

      toast.success("Integration configuration saved successfully.");
      setOpen(null);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Unable to save integration configuration"
      );
    } finally {
      setSaving(null);
    }
  };

  useEffect(() => {
    Promise.all([loadStatus()]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        className="flex justify-center py-12"
        data-testid="admin-integrations-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
      </div>
    );
  }

  const isConfigured = (id) => {
    const item = status?.[id];

    if (!item) return false;

    if (id === "whatsapp") {
      return Boolean(item.configured || item.api_configured);
    }

    return Boolean(item.configured);
  };

  return (
    <div
      className="max-w-4xl space-y-4"
      data-testid="admin-integrations"
    >
      <Card className="border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="flex items-start gap-3">
          <div className="mt-1 rounded-xl bg-white p-2 shadow-sm">
            <ShieldCheck className="h-5 w-5 text-purple-600" />
          </div>

          <div>
            <h3 className="font-display font-bold text-lg">
              Integrations & Configuration
            </h3>

            <p className="mt-1 text-sm text-muted-foreground">
              Configure API keys and service credentials directly from the
              Super Admin console. Sensitive values are stored server-side and
              are never displayed back in full.
            </p>
          </div>
        </div>
      </Card>

      {integrationList.map((integration) => {
        const configured = isConfigured(integration.id);
        const isOpen = open === integration.id;
        const values = configs[integration.id] || {};

        return (
          <Card
            key={integration.id}
            className="overflow-hidden border-border/70"
          >
            <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-purple-50 p-2.5">
                  <ShieldCheck className="h-5 w-5 text-purple-600" />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-base">
                      {integration.name}
                    </h3>

                    <Chip
                      ok={configured}
                      testId={`integration-${integration.id}`}
                    />
                  </div>

                  <p className="mt-1 text-sm text-muted-foreground">
                    {integration.description}
                  </p>
                </div>
              </div>

              <Button
                variant={isOpen ? "outline" : "default"}
                className={
                  isOpen
                    ? "shrink-0"
                    : "shrink-0 bg-gradient-to-r from-purple-600 to-pink-500"
                }
                onClick={() =>
                  isOpen
                    ? setOpen(null)
                    : loadConfig(integration.id)
                }
              >
                {isOpen ? "Close" : configured ? "Edit Configuration" : "Configure"}
              </Button>
            </div>

            {isOpen && (
              <div className="border-t bg-muted/20 p-5">
                <div className="grid gap-4">
                  {integration.fields.map((field) => (
                    <div key={field.key} className="space-y-1.5">
                      <label className="text-sm font-medium">
                        {field.label}
                      </label>

                      {field.type === "textarea" ? (
                        <Textarea
                          rows={8}
                          value={values[field.key] || ""}
                          placeholder={field.placeholder}
                          onChange={(e) =>
                            updateField(
                              integration.id,
                              field.key,
                              e.target.value
                            )
                          }
                          className="font-mono text-xs"
                        />
                      ) : (
                        <Input
                          type={field.type || "text"}
                          value={values[field.key] || ""}
                          placeholder={field.placeholder}
                          onChange={(e) =>
                            updateField(
                              integration.id,
                              field.key,
                              e.target.value
                            )
                          }
                        />
                      )}

                      {field.secret && (
                        <p className="text-[11px] text-muted-foreground">
                          Sensitive value is stored securely and will be masked
                          after saving.
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setOpen(null)}
                    disabled={saving === integration.id}
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={() => saveConfig(integration.id)}
                    disabled={saving === integration.id}
                    className="bg-gradient-to-r from-purple-600 to-pink-500"
                  >
                    {saving === integration.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Configuration
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ---------- Notifications broadcast ----------
export function NotifyTab() {
  const { enablePushNotifications } = useAuth();
  const [pushBusy, setPushBusy] = useState(false);
  const [f, setF] = useState({ title: "", body: "", audience: "all", link: "" }); const [busy, setBusy] = useState(false);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const result = await enablePushNotifications();
      if (result?.ok) {
        toast.success("Push notifications enabled on this browser");
      } else if (result?.reason === "permission_denied") {
        toast.error("Browser notification permission was denied");
      } else {
        toast.error("Unable to enable push notifications");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Unable to enable push notifications");
    } finally {
      setPushBusy(false);
    }
  };
  const send = async () => { setBusy(true); try { const { data } = await api.post("/admin/notifications/broadcast", { ...f, link: f.link || null }); toast.success(`Sent to ${data.sent} users`); } finally { setBusy(false); } };
  return (
    <div className="space-y-4">
      <Card className="p-4 border rounded-2xl bg-muted/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="font-semibold">Browser Push Notifications</div>
            <div className="text-sm text-muted-foreground">
              Enable push notifications on this admin browser to test Firebase Cloud Messaging.
            </div>
          </div>
          <Button
            type="button"
            onClick={enablePush}
            disabled={pushBusy}
            className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500"
          >
            {pushBusy ? "Enabling..." : "Enable Push Notifications"}
          </Button>
        </div>
      </Card>

      <Card className="max-w-lg space-y-3" data-testid="admin-notify"><Input placeholder="Title" className="rounded-xl" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /><Textarea placeholder="Message" className="rounded-xl" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /><div className="flex gap-2"><select className="rounded-xl border border-border bg-background px-3 py-2 text-sm" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })}><option value="all">Everyone</option><option value="customers">Customers</option><option value="vendors">Vendors</option></select><Input placeholder="Link (optional)" className="rounded-xl" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} /></div><Button onClick={send} disabled={busy || !f.title || !f.body} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send broadcast"}</Button></Card>
    </div>
  );
}

export function AuditTab() {
  const [list] = useList("/admin/audit-logs");
  return <Card className="p-0 overflow-x-auto" data-testid="admin-audit"><Table><TableHeader><TableRow><TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead><TableHead>Meta</TableHead></TableRow></TableHeader><TableBody>{list.map((a) => <TableRow key={a.id}><TableCell className="text-xs">{new Date(a.created_at).toLocaleString("en-IN")}</TableCell><TableCell>{a.actor}</TableCell><TableCell className="font-mono text-xs">{a.action}</TableCell><TableCell className="font-mono text-xs">{(a.target || "").slice(0, 12)}</TableCell><TableCell className="text-xs text-muted-foreground">{JSON.stringify(a.meta || {}).slice(0, 60)}</TableCell></TableRow>)}</TableBody></Table></Card>;
}
